import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { InvitationRead, InvitationService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { EditInvitationPermissionsDialog } from './EditInvitationPermissionsDialog';
import { InviteUserDialog } from './InviteUserDialog';
import PendingInvitationCard from './PendingInvitationCard';

const PAGE_SIZE = 21;
const DATE_FORMAT = 'LLL d, yyyy';

type ApiParams = Parameters<typeof InvitationService.invitationList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  HealthcareEntityRepGroups: 'groupNamesIn',
  HealthcareEntityRepRole: 'roleIn',
};
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  body: string;
  confirmText: string;
  confirmVariant?: 'danger' | 'primary';
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmText,
  confirmVariant = 'primary',
  isLoading = false,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogBody>
      <p className="tw:text-zinc-700">{body}</p>
    </DialogBody>
    <DialogActions>
      <Button type="button" plain onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button
        type="button"
        color={confirmVariant === 'danger' ? 'stoplight-danger' : 'brandgreen1'}
        onClick={onConfirm}
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

const ActionCell: React.FC<{
  invitation: InvitationRead;
  queryKeyBase: string;
  showOrganization: boolean;
}> = ({ invitation, queryKeyBase, showOrganization }) => {
  const queryClient = useQueryClient();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const canRead = useHasPermission('read__accounts__invitation');
  const canUpdate = useHasPermission('update__accounts__invitation');
  const canCancel = useHasPermission('delete__accounts__invitation');

  const resendMutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationResendUpdate({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase],
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationDestroy({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase],
      });
    },
  });

  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync();
      toast.success('Invitation resent successfully');
      setShowResendDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync();
      toast.success('Invitation canceled successfully');
      setShowCancelDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const email = invitation.email || 'this user';

  return (
    <>
      <div className="tw:flex tw:justify-start tw:gap-2">
        {canUpdate && (
          <TooltippedText tooltipContent="Resend">
            <Button
              small
              color="brand-green-outline"
              onClick={(e) => {
                e.stopPropagation();
                setShowResendDialog(true);
              }}
              title="Resend"
              data-testid={`invitation-resend-button-${invitation.id}`}
            >
              <i className="far fa-fw fa-paper-plane" />
            </Button>
          </TooltippedText>
        )}
        {canRead && (
          <TooltippedText tooltipContent={canUpdate ? 'Edit' : 'View'}>
            <Button
              small
              color="brand-green-outline"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditDialog(true);
              }}
              title={canUpdate ? 'Edit' : 'View'}
              data-testid={`invitation-edit-button-${invitation.id}`}
            >
              <i className="far fa-fw fa-user-lock" />
            </Button>
          </TooltippedText>
        )}
        {canCancel && (
          <TooltippedText tooltipContent="Cancel">
            <Button
              small
              color="stoplight-danger-outline"
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelDialog(true);
              }}
              title="Cancel"
              data-testid={`invitation-cancel-button-${invitation.id}`}
            >
              <i className="far fa-fw fa-trash-can" />
            </Button>
          </TooltippedText>
        )}
      </div>

      <EditInvitationPermissionsDialog
        invitation={invitation}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        queryKeyBase={queryKeyBase}
        showOrganization={showOrganization}
      />

      <ConfirmationDialog
        open={showResendDialog}
        onClose={() => setShowResendDialog(false)}
        onConfirm={handleResend}
        title="Resend Invitation"
        body={`Are you sure you want to resend the invitation to ${email}?`}
        confirmText="Resend Invitation"
        isLoading={resendMutation.isPending}
      />

      <ConfirmationDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancel}
        title="Undo Invitation"
        body={`Are you sure you want to cancel the invitation to ${email}?`}
        confirmText="Undo Invitation"
        confirmVariant="danger"
        isLoading={cancelMutation.isPending}
      />
    </>
  );
};

export type PendingInvitationsListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  usersRoute: string;
  showOrganization?: boolean;
  showOrganizationFilter?: boolean;
  linkContext?: 'organization' | 'staff';
};

export const PendingInvitationsListPage: React.FC<PendingInvitationsListPageProps> = ({
  title = 'Pending Invitations',
  queryKeyBase,
  displayTypePrefKey,
  usersRoute,
  showOrganization = false,
  showOrganizationFilter = false,
}) => {
  useDocumentTitle(title);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const requireActingAs = useRequireActingAsEntity();
  const canInvite = useHasPermission('create__accounts__invitation');

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const healthcareEntity = filterValues.healthcare_entity || '';
  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-created_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        categories: filterValues.categories,
        booleanFilters: filterValues.booleanFilters,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.categories,
      filterValues.booleanFilters,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [queryKeyBase, search, categoryFilters, ordering, healthcareEntity],
    [queryKeyBase, search, categoryFilters, ordering, healthcareEntity]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) => {
        return InvitationService.invitationList({
          search: search || undefined,
          healthcareEntityId: showOrganization && healthcareEntity ? healthcareEntity : undefined,
          ...categoryApiParams,
          ordering: ordering as ApiParams['ordering'],
          limit: PAGE_SIZE,
          offset: pageParam as number,
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.next) {
          return allPages.length * PAGE_SIZE;
        }
        return undefined;
      },
    });

  const invitations = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreInvitations = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreInvitations, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: roleLabelMap = {} } = useCodeDefinitionsAsMap('HealthcareEntityRepRole');

  return (
    <main className={MAIN_CONTENT_AREA} data-testid="pending-invitations-list-page">
      <div className={STICKY_HEADER}>
        <div className={PAGE_HEADER}>
          <Heading level={1} className="tw:!text-brand-green-0">
            <i className="far fa-fw fa-envelope tw:mr-1" />
            {title}
          </Heading>
          <div className="tw:flex tw:gap-2">
            <Button
              color="brandgreen1"
              onClick={() => navigate(usersRoute)}
              data-testid="pending-invitations-users-link"
            >
              Users List
            </Button>
            {canInvite && (
              <Button
                color="brandgreen1"
                onClick={() => requireActingAs(() => setShowInviteDialog(true))}
                data-testid="pending-invitations-invite-button"
              >
                Invite User
              </Button>
            )}
            <InviteUserDialog open={showInviteDialog} onClose={() => setShowInviteDialog(false)} />
          </div>
        </div>

        {isError && (
          <div className="tw:mt-4 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
            <p className="tw:font-semibold">Something went wrong</p>
            <p className="tw:text-sm">
              Please try again later. If the problem persists, please contact us.
            </p>
          </div>
        )}

        <div className="tw:mt-1">
          <ListFilter
            show_search={true}
            categories={Array.from(CATEGORIES)}
            showStatus={false}
            showReason={false}
            showOrganizationFilter={showOrganizationFilter}
            showDisplayType={true}
            labelOverrides={{
              HealthcareEntityRepGroups: 'Permissions',
              HealthcareEntityRepRole: 'Role',
            }}
            sortOptions={[
              ...(showOrganization
                ? [{ label: 'Organization', value: 'healthcare_entity__name' }]
                : []),
              { label: 'Email', value: 'email' },
              { label: 'Role', value: 'role' },
              { label: 'Invited On', value: 'created_at' },
            ]}
            values={filterValues}
            onFilterChange={updateFilters}
          />
        </div>
      </div>

      <div className="tw:mt-3">
        {isLoading && invitations.length === 0 && (
          <div className="tw:text-center tw:py-8">Loading invitations...</div>
        )}
        {!isLoading && invitations.length === 0 ? (
          <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
            No pending invitations.
            <div className="tw:text-zinc-500 tw:italic tw:text-sm">
              Invite someone to get started.
            </div>
          </div>
        ) : (
          <>
            {invitations.length > 0 && (
              <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                Showing {invitations.length} of {totalCount} invitations
              </div>
            )}

            {displayType === 'card' ? (
              <div className="tw:@container">
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                  {invitations.map((invitation) => (
                    <div key={invitation.id}>
                      <PendingInvitationCard
                        invitation={invitation}
                        roleLabelMap={roleLabelMap}
                        showOrganization={showOrganization}
                        actions={
                          <ActionCell
                            invitation={invitation}
                            queryKeyBase={queryKeyBase}
                            showOrganization={showOrganization}
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <Table striped dense>
                <TableHead>
                  <TableSortHeader
                    sortKey="email"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Email
                  </TableSortHeader>
                  {showOrganization && (
                    <TableSortHeader
                      sortKey="healthcare_entity__name"
                      ordering={ordering}
                      onOrderingChange={setOrdering}
                    >
                      Organization
                    </TableSortHeader>
                  )}
                  <TableSortHeader
                    sortKey="role"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Role
                  </TableSortHeader>
                  <TableSortHeader
                    sortKey="created_at"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Invited On
                  </TableSortHeader>
                  <TableHeader>Actions</TableHeader>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow
                      key={invitation.id}
                      data-testid={`pending-invitation-row-${invitation.id}`}
                    >
                      <TableCell>{invitation.email || '—'}</TableCell>
                      {showOrganization && (
                        <TableCell data-testid={`pending-invitation-row-org-${invitation.id}`}>
                          {invitation.healthcare_entity_name || '—'}
                        </TableCell>
                      )}
                      <TableCell>
                        {invitation.role ? roleLabelMap[invitation.role] || invitation.role : '—'}
                      </TableCell>
                      <TableCell>
                        {invitation.created_at
                          ? format(parseISO(invitation.created_at), DATE_FORMAT)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <ActionCell
                          invitation={invitation}
                          queryKeyBase={queryKeyBase}
                          showOrganization={showOrganization}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {hasNextPage && (
              <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                {isFetchingNextPage && (
                  <div className="tw:text-center tw:text-zinc-500">Loading more invitations...</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};
